/*
 * Paying in the app (CLAUDE.md §4, Saif 24 Oct 2026; docs/32): Apple Pay on the iPhone and Google Pay on Android through
 * Stripe's native sheet (@capacitor-community/stripe over Stripe's iOS and Android SDKs). The website never loads this
 * file's plugin: it keeps the Express Checkout Element. The server creates the PaymentIntent on the site's connected
 * account exactly as for the website and answers with the client secret, the account and the platform's publishable key;
 * the sheet confirms the intent on that key with the account set (as Stripe.js does on the web), and any 3-D Secure runs
 * inside it. Nothing here decides an amount: the summary line shows the intent's own total.
 */
import { NATIVE, PLATFORM } from './platform.js';

export type NativeWallets = { applePay: boolean; googlePay: boolean };

/** The Merchant ID registered with Apple (scripts/native/apple-pay.mjs). The server's answer names it; this covers an API older than the app. */
export const APPLE_PAY_MERCHANT_ID = 'merchant.com.tikkas.app';

/** The native sheet is for the iPhone and Android apps only; the website (and the app's files opened in a browser) keep Stripe.js. */
export const NATIVE_SHEET = NATIVE && (PLATFORM === 'ios' || PLATFORM === 'android');

type Plugin = typeof import('@capacitor-community/stripe');
let mod: Promise<Plugin> | null = null;
/** The plugin's JavaScript is loaded only in the app, so the website's bundle keeps it out of the way. */
const plugin = (): Promise<Plugin> => (mod ??= import('@capacitor-community/stripe'));

let initialised: string | null = null;
/**
 * `initialize` once per key and account (Stripe's SDK keeps them until told otherwise).
 * Never return the plugin itself from an async function: Capacitor's plugin object is a Proxy that answers every
 * property, `then` included, with a method wrapper, so a promise resolved with it treats it as a thenable and waits
 * for ever (found on the simulator, 24 Oct 2026). Callers take the plugin from `plugin()` themselves.
 */
async function ready(publishableKey: string, stripeAccountId: string): Promise<void> {
  const { Stripe } = await plugin();
  const key = `${publishableKey}:${stripeAccountId}`;
  if (initialised !== key) {
    await Stripe.initialize({ publishableKey, stripeAccount: stripeAccountId });
    initialised = key;
    console.log('[tikkas] native sheet initialised', { stripeAccountId });
  }
}

/** Which wallet this phone can use: Apple Pay on iOS, Google Pay on Android. The plugin rejects when there is none. */
export async function nativeWallets(): Promise<NativeWallets> {
  if (!NATIVE_SHEET) return { applePay: false, googlePay: false };
  const { Stripe } = await plugin();
  const yes = () => true, no = () => false;
  if (PLATFORM === 'ios') return { applePay: await Stripe.isApplePayAvailable().then(yes, no), googlePay: false };
  return { applePay: false, googlePay: await Stripe.isGooglePayAvailable().then(yes, no) };
}

export type SheetOutcome = 'completed' | 'cancelled';

export interface SheetArgs {
  publishableKey: string;
  stripeAccountId: string;
  clientSecret: string;
  /** The intent's amount, for the summary line only (the server set the intent's amount). */
  amountPence: number;
  applePayMerchantId: string;
  /** The summary line's name: the shop. */
  label: string;
}

/** Pounds for the sheet's summary line: Apple and Google show the major unit, two places. */
export const sheetAmount = (pence: number): number => Number((pence / 100).toFixed(2));

/** The plugin's answer, as a result or a readable error. */
export function sheetOutcome(result: string): SheetOutcome {
  if (result === 'applePayCompleted' || result === 'googlePayCompleted') return 'completed';
  if (result === 'applePayCanceled' || result === 'googlePayCanceled') return 'cancelled';
  throw new Error('The payment did not go through. Try again, or pay another way.');
}

/** Shows the sheet for one intent and waits for the customer. Throws with a readable message when the payment fails. */
export async function presentNativeWallet(a: SheetArgs): Promise<SheetOutcome> {
  const { Stripe } = await plugin();
  await ready(a.publishableKey, a.stripeAccountId);
  const summary = { paymentSummaryItems: [{ label: a.label, amount: sheetAmount(a.amountPence) }], merchantIdentifier: a.applePayMerchantId, countryCode: 'GB', currency: 'GBP' };
  // The steps are logged (no secrets) so a support question can be answered from the device console.
  if (PLATFORM === 'ios') {
    await Stripe.createApplePay({ paymentIntentClientSecret: a.clientSecret, ...summary });
    console.log('[tikkas] native sheet created', { amount: summary.paymentSummaryItems[0]?.amount, merchant: a.applePayMerchantId });
    const { paymentResult } = await Stripe.presentApplePay();
    console.log('[tikkas] native sheet result', paymentResult);
    return sheetOutcome(paymentResult);
  }
  await Stripe.createGooglePay({ paymentIntentClientSecret: a.clientSecret, ...summary });
  console.log('[tikkas] native sheet created', { amount: summary.paymentSummaryItems[0]?.amount });
  const { paymentResult } = await Stripe.presentGooglePay();
  console.log('[tikkas] native sheet result', paymentResult);
  return sheetOutcome(paymentResult);
}
